import React, { FC, FormEvent } from 'react'

import { Button, ButtonSizes } from '@inno-ui/button'
import { CheckmarkIcon, IconSizes, LockLineIcon } from '@inno-ui/icons'
import { TextInput, TextInputSizes } from '@inno-ui/text-input'
import { toast } from '@inno-ui/toast'
import { useForm, useStore } from '@tanstack/react-form'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { ErrorResponse } from 'shared/api/auth/auth.types'
import { useChangePasswordMutation } from 'shared/api/auth/requests'
import { PasswordRequirements } from 'shared/components/PasswordRequirements'
import { i18nextContainer } from 'shared/configs/i18n/config'
import { getFieldError } from 'shared/utils'

import { EditProfileSection } from '../EditProfileSection'
import styles from './ChangePasswordLayer.module.scss'
import { changePasswordSchema } from './ChangePasswordLayer.schema'
import { ChangePasswordLayerProps } from './ChangePasswordLayer.types'

const ChangePasswordLayer: FC<ChangePasswordLayerProps> = ({ onClose }) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'my_profile_page.change_password'
  })

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    },
    validators: {
      onMount: changePasswordSchema,
      onChange: changePasswordSchema,
      onSubmit: changePasswordSchema
    },
    onSubmit: values => {
      changePasswordMutation.mutate({
        current_password: values.value.currentPassword,
        new_password: values.value.newPassword,
        new_password_repeat: values.value.confirmNewPassword
      })
    }
  })

  const changePasswordMutation = useChangePasswordMutation({
    onError: error => {
      const axiosError = error as AxiosError<ErrorResponse>
      const status = axiosError.response?.status
      if (status === 400) {
        toast.error({
          title: t('error.title'),
          subTitle: t('error.invalid_password')
        })
      } else {
        toast.error({
          title: t('error.title'),
          subTitle: t('error.generic_error')
        })
      }
    },
    onSuccess: () => {
      form.reset()
      toast.success({
        title: t('success.title'),
        subTitle: t('success.description')
      })
      onClose()
    }
  })

  const canSubmit = useStore(form.store, state => state.canSubmit)

  const isSubmitted = useStore(
    form.store,
    state => state.submissionAttempts > 0
  )

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    form.handleSubmit()
  }

  return (
    <form onSubmit={handleSubmit} className={styles.changePasswordLayer}>
      <EditProfileSection
        title={t('old_password.title')}
        icon={<LockLineIcon size={IconSizes.Size34} />}
      >
        <form.Field name='currentPassword'>
          {field => {
            const error = getFieldError(field, {
              isSubmitted,
              requireTouched: true
            })
            return (
              <TextInput
                placeholder={t('old_password.placeholder')}
                type='password'
                size={TextInputSizes.L}
                value={field.state.value}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                errorText={error}
                hasError={!!error}
              />
            )
          }}
        </form.Field>
      </EditProfileSection>
      <EditProfileSection
        title={t('new_password.title')}
        icon={<LockLineIcon size={IconSizes.Size34} />}
      >
        <form.Field name='newPassword'>
          {field => {
            const error = getFieldError(field, {
              isSubmitted,
              requireTouched: true
            })
            return (
              <>
                <TextInput
                  placeholder={t('new_password.new_password_placeholder')}
                  type='password'
                  size={TextInputSizes.L}
                  value={field.state.value}
                  onChange={e => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  // The checklist already explains rule failures; keep other errors like "same as current"
                  errorText={
                    error === i18nextContainer.t('common:validation.password')
                      ? undefined
                      : error
                  }
                  hasError={!!error}
                />
                <PasswordRequirements password={field.state.value} />
              </>
            )
          }}
        </form.Field>
        <form.Field name='confirmNewPassword'>
          {field => {
            const error = getFieldError(field, {
              isSubmitted,
              requireTouched: true
            })
            return (
              <TextInput
                placeholder={t('new_password.repeat_new_password_placeholder')}
                type='password'
                size={TextInputSizes.L}
                value={field.state.value}
                onChange={e => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                errorText={error}
                hasError={!!error}
              />
            )
          }}
        </form.Field>
      </EditProfileSection>
      <EditProfileSection>
        <Button
          label={t('submit_button')}
          icon={CheckmarkIcon}
          size={ButtonSizes.L}
          type='submit'
          disabled={!canSubmit}
          isLoading={changePasswordMutation.isPending}
        />
      </EditProfileSection>
    </form>
  )
}

export { ChangePasswordLayer }
